// Global Instructions Rule Applied!
//
// Configuration → Agent Oversight admin models.
//
// Shape mirrors public.view__agent_access_admin — the cross-user read behind
// GET /config/agent-access: every user's grants (rights + expiry), today's
// usage vs caps, and the newest agent proposals across users.
//
// TWO HALVES, TWO POSTURES.
//   * connections / usage — display-only derived data with no edit path, so
//     they are typed rather than Zod-validated (the same deliberate choice as
//     the usage half of agent_gateway_config_model.ts).
//   * recent_activity — NOT display-only since 2026-09-02: an admin reviews,
//     approves, rejects, retries and undoes those rows, so they are the same
//     proposal records the owner screen posts back, and they are validated
//     with the SAME schema plus the user each was filed for. Reusing
//     AgentProposalSchema is the point — both screens render through one set
//     of components, so a key that existed on only one of them would be a
//     review form with a field missing.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'
import { AgentProposalSchema } from './agent_access_model.ts'

export interface AgentAccessAdminConnection {
  agent_grants__id: string
  agent_grants__agent_id: string
  prog__agent_name: string
  // Resolved system_users name, falling back to the auth email; may be null
  // for an orphaned grant whose auth user is gone.
  prog__user_name: string | null
  // Verbatim grant scopes: { mode, tools: string[], live_tools: string[] }.
  agent_grants__scopes: {
    mode?: string
    tools?: string[]
    live_tools?: string[]
  } | null
  prog__grant_status: 'active' | 'expired' | 'revoked'
  agent_grants__expires_at: string
  agent_grants__date_created: string
}

export interface AgentAccessAdminWriteCap {
  tool_key: string
  prog__max_per_day: number
  // ENFORCEMENT PARITY (see the RPC header): the grant-wide, trashed-inclusive
  // proposal count since UTC midnight — the exact number agent_create_proposal
  // compares against max_per_day. The same value repeats on each tool row of
  // one grant on purpose.
  prog__created_today: number
}

export interface AgentAccessAdminUsageRow {
  agent_grants__id: string
  agent_grants__agent_id: string
  prog__agent_name: string
  prog__user_name: string | null
  prog__grant_status: 'active' | 'expired' | 'revoked'
  prog__requests_today: number
  prog__rows_returned_today: number
  prog__peak_requests_per_minute: number
  // 0 = uncapped for both.
  agents__requests_per_minute: number
  agents__daily_row_limit: number
  prog__write_caps: AgentAccessAdminWriteCap[]
}

// The owner's proposal row plus the user it was filed for. .extend() keeps
// .strict(), so a key the RPC adds without landing here is still an error.
export const AgentProposalAdminSchema = AgentProposalSchema.extend({
  prog__user_name: z.string().nullable(),
  // Lets the review drawer say "on behalf of <user>" only when the row really
  // is somebody else's — an admin's own proposals appear in this list too.
  prog__user_is_me: z.boolean()
})

export type AgentProposalAdmin = z.infer<typeof AgentProposalAdminSchema>

export const AgentProposalAdminListSchema = z.array(AgentProposalAdminSchema)

export function validateAgentProposalAdminList(data: unknown): AgentProposalAdmin[] {
  return validateWithZodSchema(AgentProposalAdminListSchema, data, 'agent oversight activity')
}

export interface AgentAccessAdminData {
  connections: AgentAccessAdminConnection[]
  usage: AgentAccessAdminUsageRow[]
  recent_activity: AgentProposalAdmin[]
}

// ── History (Agent Oversight period reads) ──────────────────────────────────
//
// view__agent_usage_admin — Zod-validated (unlike the "right now" usage
// above): these are the numbers the Usage section and the Connections
// period columns are built from, so a shape drift must fail loudly.
const count = z.number().int().nonnegative()

export const AgentUsagePeriodSchema = z
  .object({ prog__date_from: z.string(), prog__date_to: z.string() })
  .strict()

export const AgentUsageTotalsSchema = z
  .object({
    prog__calls: count,
    prog__rows_returned: count,
    prog__active_connections: count,
    prog__active_users: count,
    prog__writes: count,
    prog__failed_writes: count
  })
  .strict()

export const AgentUsageDaySchema = z
  .object({
    prog__day: z.string(),
    prog__calls: count,
    prog__rows_returned: count,
    prog__writes: count,
    prog__failed_writes: count
  })
  .strict()

export const AgentUsageGrantSchema = z
  .object({
    agent_grants__id: z.string(),
    agent_grants__agent_id: z.string(),
    prog__agent_name: z.string(),
    prog__user_name: z.string().nullable(),
    prog__grant_status: z.enum(['active', 'expired', 'revoked']),
    prog__calls: count,
    prog__rows_returned: count,
    prog__writes: count,
    prog__failed_writes: count,
    // All-time, not period; null for a grant that never made a call.
    prog__last_call_at: z.string().nullable()
  })
  .strict()

export const AgentUsageToolSchema = z
  .object({
    prog__tool_key: z.string(),
    prog__tool_kind: z.string(),
    prog__calls: count,
    prog__rows_returned: count,
    prog__connections: count,
    prog__last_call_at: z.string().nullable()
  })
  .strict()

export const AgentUsageAdminSchema = z
  .object({
    period: AgentUsagePeriodSchema,
    totals: AgentUsageTotalsSchema,
    by_day: z.array(AgentUsageDaySchema),
    by_grant: z.array(AgentUsageGrantSchema),
    by_tool: z.array(AgentUsageToolSchema)
  })
  .strict()

export type AgentUsageDay = z.infer<typeof AgentUsageDaySchema>
export type AgentUsageGrant = z.infer<typeof AgentUsageGrantSchema>
export type AgentUsageTool = z.infer<typeof AgentUsageToolSchema>
export type AgentUsageAdmin = z.infer<typeof AgentUsageAdminSchema>

export function validateAgentUsageAdmin(data: unknown): AgentUsageAdmin {
  return validateWithZodSchema(AgentUsageAdminSchema, data, 'agent oversight usage')
}

/** The Activity list's server-side filters (view__agent_proposals_admin). */
export type AgentActivityStatusGroup = 'pending' | 'applied' | 'failed' | 'other'

export interface AgentActivityQuery {
  status?: AgentActivityStatusGroup
  grant_id?: string
  tool?: string
  live?: boolean
  search?: string
  date_from?: string
  date_to?: string
  limit?: number
  offset?: number
}

export interface AgentActivityPage {
  data: AgentProposalAdmin[]
  has_more: boolean
  count: number
}

// ── Call log (view__agent_call_log_admin / view__agent_call_log_insights) ───
export const AGENT_CALL_OUTCOMES = [
  'ok',
  'empty',
  'not_found',
  'invalid_args',
  'refused_scope',
  'refused_cap',
  'refused_grant',
  'error'
] as const
export type AgentCallOutcome = (typeof AGENT_CALL_OUTCOMES)[number]

const jsonValue: z.ZodType<unknown> = z.unknown()

export const AgentCallLogRowSchema = z
  .object({
    agent_call_log__id: z.string(),
    agent_call_log__called_at: z.string(),
    agent_call_log__grant_id: z.string().nullable(),
    agent_call_log__agent_key: z.string(),
    prog__agent_name: z.string(),
    prog__user_name: z.string().nullable(),
    agent_call_log__tool_key: z.string(),
    agent_call_log__tool_kind: z.enum(['read', 'write', 'resource']),
    agent_call_log__args: z.record(z.string(), jsonValue),
    agent_call_log__args_truncated: z.boolean(),
    agent_call_log__outcome: z.enum(AGENT_CALL_OUTCOMES),
    agent_call_log__error_code: z.string().nullable(),
    agent_call_log__error: z.string().nullable(),
    agent_call_log__row_count: count.nullable(),
    agent_call_log__truncated: z.boolean().nullable(),
    agent_call_log__has_more: z.boolean().nullable(),
    agent_call_log__duration_ms: count.nullable()
  })
  .strict()

export type AgentCallLogRow = z.infer<typeof AgentCallLogRowSchema>

export function validateAgentCallLogRows(data: unknown): AgentCallLogRow[] {
  return validateWithZodSchema(z.array(AgentCallLogRowSchema), data, 'agent call log')
}

export const AgentToolHealthSchema = z
  .object({
    prog__tool_key: z.string(),
    prog__tool_kind: z.string(),
    prog__calls: count,
    prog__ok: count,
    prog__empty: count,
    prog__not_found: count,
    prog__invalid_args: count,
    prog__refused: count,
    prog__error: count,
    prog__p50_ms: count.nullable(),
    prog__p95_ms: count.nullable()
  })
  .strict()

export const AgentTopFailureSchema = z
  .object({
    prog__tool_key: z.string(),
    prog__outcome: z.enum(AGENT_CALL_OUTCOMES),
    prog__error_code: z.string().nullable(),
    prog__calls: count,
    prog__last_at: z.string(),
    prog__example_args: z.record(z.string(), jsonValue),
    prog__example_error: z.string().nullable()
  })
  .strict()

export const AgentArgUsageSchema = z
  .object({
    prog__tool_key: z.string(),
    prog__arg_key: z.string(),
    prog__supplied: count,
    prog__tool_calls: count,
    prog__top_values: z.array(z.object({ value: z.string(), count }).strict())
  })
  .strict()

export const AgentEmptySearchSchema = z
  .object({
    prog__query: z.string(),
    prog__entity_type: z.string(),
    prog__calls: count,
    prog__last_at: z.string()
  })
  .strict()

export const AgentScopeRefusalSchema = z
  .object({
    prog__tool_key: z.string(),
    prog__calls: count,
    prog__users: count,
    prog__last_at: z.string()
  })
  .strict()

export const AgentCallInsightsSchema = z
  .object({
    period: AgentUsagePeriodSchema,
    tool_health: z.array(AgentToolHealthSchema),
    top_failures: z.array(AgentTopFailureSchema),
    arg_usage: z.array(AgentArgUsageSchema),
    empty_searches: z.array(AgentEmptySearchSchema),
    scope_refusals: z.array(AgentScopeRefusalSchema)
  })
  .strict()

export type AgentToolHealth = z.infer<typeof AgentToolHealthSchema>
export type AgentTopFailure = z.infer<typeof AgentTopFailureSchema>
export type AgentArgUsage = z.infer<typeof AgentArgUsageSchema>
export type AgentEmptySearch = z.infer<typeof AgentEmptySearchSchema>
export type AgentScopeRefusal = z.infer<typeof AgentScopeRefusalSchema>
export type AgentCallInsights = z.infer<typeof AgentCallInsightsSchema>

export function validateAgentCallInsights(data: unknown): AgentCallInsights {
  return validateWithZodSchema(AgentCallInsightsSchema, data, 'agent call insights')
}

export interface AgentCallLogQuery {
  grant_id?: string
  tool?: string
  kind?: 'read' | 'write' | 'resource'
  outcomes?: AgentCallOutcome[]
  search?: string
  date_from?: string
  date_to?: string
  limit?: number
  offset?: number
}

export interface AgentCallLogPage {
  data: AgentCallLogRow[]
  has_more: boolean
  count: number
}
